import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { fetchWithAuth } from '../services/api';

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ fullName: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setIsSubmitting(true);
    try {
      await fetchWithAuth('/contact-messages', { method: 'POST', body: formData, isPublic: true });
      setFormData({ fullName: '', email: '', subject: '', message: '' });
      setStatus({ type: 'success', message: t('contactPage.form.submitSuccess') });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || t('contactPage.form.submitError') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  return (
    <div className="bg-gray-50 py-12 sm:py-16">
      <SEO page="contact" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">{t('contactPage.title', 'Contact Us')}</h1>
          <p className="mt-3 text-lg text-gray-600">
            {t('contactPage.subtitle', 'We are here to help. Reach out to us with any questions or inquiries.')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          {/* Contact Information Section */}
          <div className="bg-white p-8 rounded-xl shadow-lg space-y-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">{t('contactPage.info.title', 'Our Contact Information')}</h2>
            
            <div className="flex items-start space-x-4">
              <MapPinIcon className="h-8 w-8 text-indigo-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-medium text-gray-700">{t('contactPage.info.addressTitle', 'Address')}</h3>
                <p className="text-gray-600">{t('contactPage.info.addressStreet', 'Улица Македонија бб')}</p>
                <p className="text-gray-600">{t('contactPage.info.addressCity', '1000 Скопје, Македонија')}</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <PhoneIcon className="h-8 w-8 text-indigo-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-medium text-gray-700">{t('contactPage.info.phoneTitle', 'Phone')}</h3>
                <a href="tel:+38970123456" className="text-indigo-600 hover:text-indigo-800 transition-colors">{t('contactPage.info.phoneNumber', '+389 70 228 298')}</a>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <EnvelopeIcon className="h-8 w-8 text-indigo-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-medium text-gray-700">{t('contactPage.info.emailTitle', 'Email')}</h3>
                <a href="mailto:info@spomen.mk" className="text-indigo-600 hover:text-indigo-800 transition-colors">{t('contactPage.info.emailAddress', 'info@spomen.mk')}</a>
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">{t('contactPage.form.title', 'Send Us a Message')}</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">{t('contactPage.form.fullNameLabel', 'Full Name')}</label>
                <input type="text" name="fullName" id="fullName" autoComplete="name" required maxLength="120" value={formData.fullName} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder={t('contactPage.form.fullNamePlaceholder', 'Вашето име и презиме')}/>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('contactPage.form.emailLabel', 'Email Address')}</label>
                <input type="email" name="email" id="email" autoComplete="email" required maxLength="254" value={formData.email} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder={t('contactPage.form.emailPlaceholder', 'your@email.com')}/>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700">{t('contactPage.form.subjectLabel', 'Subject')}</label>
                <input type="text" name="subject" id="subject" maxLength="200" value={formData.subject} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder={t('contactPage.form.subjectPlaceholder', 'Наслов на пораката')}/>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">{t('contactPage.form.messageLabel', 'Message')}</label>
                <textarea name="message" id="message" rows="4" required maxLength="5000" value={formData.message} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder={t('contactPage.form.messagePlaceholder', 'Вашата порака овде...')}></textarea>
              </div>
              {status && (
                <p role="status" className={status.type === 'success' ? 'text-sm text-green-700' : 'text-sm text-red-700'}>
                  {status.message}
                </p>
              )}
              <div>
                <button type="submit" disabled={isSubmitting} className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">
                  {isSubmitting ? t('contactPage.form.submitting') : t('contactPage.form.submitButton', 'Send Message')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
